<template>
  <template v-if="itemType === 'video'">
    <!-- Default: just the playhead time + range toggle button -->
    <template v-if="!settingRange && capturedTimestamp == null">
      <div class="composer-timestamp is-live">
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'clock']" />
        </span>
        {{ formatTimestamp(currentTime) }}
      </div>
      <button
        class="button is-small is-info range-toggle-btn"
        title="Set a time range"
        @click="settingRange = true"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'pipe-section']" />
        </span>
      </button>
    </template>

    <!-- Setting range, no start yet: live playhead + Set Start -->
    <template v-else-if="capturedTimestamp == null">
      <div class="composer-timestamp is-live">
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'clock']" />
        </span>
        {{ formatTimestamp(currentTime) }}
      </div>
      <button
        class="button is-small is-outlined is-primary set-range-btn"
        @click="$emit('set-start')"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'arrow-right-from-line']" />
        </span>
        <span>Set Start</span>
      </button>
      <button
        class="button is-small cancel-btn"
        title="Cancel range"
        @click="cancelRange"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'xmark']" />
        </span>
      </button>
    </template>

    <!-- Start captured, no end yet: locked start + live playhead + Set End -->
    <template v-else-if="capturedTimestampEnd == null">
      <div class="composer-timestamp">
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'clock']" />
        </span>
        {{ formatTimestamp(capturedTimestamp) }}
      </div>
      <span class="timestamp-dash">-</span>
      <div class="composer-timestamp is-live">
        {{ formatTimestamp(currentTime) }}
      </div>
      <button
        class="button is-small is-outlined is-danger set-range-btn"
        @click="$emit('set-end')"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'arrow-right-to-line']" />
        </span>
        <span>Set End</span>
      </button>
      <button
        class="button is-small cancel-btn"
        title="Cancel range"
        @click="cancelRange"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'xmark']" />
        </span>
      </button>
    </template>

    <!-- Both start + end captured: show final range -->
    <template v-else>
      <div class="composer-timestamp">
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'clock']" />
        </span>
        {{ formatTimestamp(capturedTimestamp) }}
        <span class="timestamp-separator">-</span>
        {{ formatTimestamp(capturedTimestampEnd) }}
        <button class="clear-btn" @click="cancelRange" title="Remove time range">
          <font-awesome-icon :icon="['fal', 'xmark']" />
        </button>
      </div>
    </template>
  </template>
</template>

<script setup>
import { ref, watch } from 'vue'
import { formatTimestamp } from '@/utils/reviewUtils'

const props = defineProps({
  itemType: { type: String, default: 'video' },
  currentTime: { type: Number, default: 0 },
  capturedTimestamp: { type: Number, default: null },
  capturedTimestampEnd: { type: Number, default: null },
})

const emit = defineEmits(['set-start', 'set-end', 'clear'])

const settingRange = ref(false)

function cancelRange() {
  settingRange.value = false
  emit('clear')
}

// If timestamps are cleared externally, reset local state
watch(() => props.capturedTimestamp, (val) => {
  if (val == null) settingRange.value = false
})
</script>

<style lang="scss" scoped>
.composer-timestamp {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #fff;
  font-size: 0.75rem;
  padding: 0.25em 0.75em;
  border-radius: 4px;
  height: 2em;
  background: var(--bulma-success, #48c78e);

  &.is-live {
    background: var(--bulma-grey, #7a7a7a);
  }
}

.timestamp-separator {
  margin: 0 0.15rem;
}

.timestamp-dash {
  color: var(--bulma-text-weak, #999);
  font-size: 0.75rem;
  font-weight: 600;
}

.clear-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0 0 0 0.25rem;
  font-size: 0.7rem;

  &:hover {
    color: #fff;
  }
}

.range-toggle-btn {
  height: 2em;
  width: 2em;
  padding: 0;
}

.set-range-btn {
  font-size: 0.75rem;
  white-space: nowrap;
  height: 2em;
  line-height: 1;
}

.cancel-btn {
  height: 2em;
  width: 2em;
  padding: 0;
}
</style>
